import { FormControlLabel } from "@mui/material";
import lodash from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

//COMPONENTS
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogCheckbox from "@/main/components/shared/checkbox/kogCheckbox";
import { SkeletonLoading } from "@/main/components/shared/styles";

//MODELS/API
import { quoteItemApi } from "@/main/api";
import { CancelableQuoteItemResponse, CancelableQuoteItemsResponse } from "@/main/models/response";

//REDUX/UTILS
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";

// STYLES/COMPONENTS LOCAL
import { CartState } from "@/main/store/cart";
import ModalConfirmCancelPart from "../modalConfirmCancelPart/modalConfirmCancelPart";
import AreaPartsSelectCancel from "./areaPartsSelectCancel/areaPartsSelectCancel";
import * as S from "./styles";
import { RefreshQuoteByGetItemsType } from "../types";

interface IModalCancelPartsProps {
	isOpenModal: boolean;
	numbersParts?: number;
	guidQuote: string;
	dataCart: CartState;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	refreshQuoteByGetItems: RefreshQuoteByGetItemsType;
	closeModal: () => void;
}
function ModalCancelParts({
	isOpenModal,
	guidQuote,
	dataCart,
	refreshQuoteByGetItems,
	closeModal,
}: IModalCancelPartsProps) {
	const cartNoSupplierAssigned = dataCart.cartItems.dataNoSupplierAssigned;
	const cartDataUnansweredItems = dataCart.cartItems.dataUnansweredItems;

	const [dataParts, setDataParts] = useState<CancelableQuoteItemsResponse | null>(null);
	const [isLoadingParts, setIsLoadingParts] = useState(false);
	const [isLoadingSave] = useState(false);
	const [optionsSelected, setOptionsSelected] = useState<string[]>([]);
	const [openModalConfirmCancel, setOpenModalConfirmCancel] = useState<boolean>(false);

	const allCheckboxesSelected = useMemo(() => {
		if (isNullUndefined(dataParts)) return false;
		const allGuidsUnaswared = dataParts.cancelableQuoteItem.map((item) => item.guidQuoteItem);
		const allGuidsAbovePriceCap = dataParts.cancelableAbovePriceCapQuoteItem.map((item) => item.guidQuoteItem);

		const allGuids = [...allGuidsUnaswared, ...allGuidsAbovePriceCap];

		return allGuids.every((item) => optionsSelected.includes(item));
	}, [JSON.stringify(optionsSelected)]);

	//#region Core functions
	function validateForm() {
		if (optionsSelected.length === 0) {
			const messageError = getTranslateByKey("information.selectOne", true).replace(
				"{0}",
				getTranslateByKey("information.part").toLowerCase(),
			);
			toast.error(messageError);
			return false;
		}
		return true;
	}
	async function fetchPartsCancel() {
		if (isLoadingParts) return;

		setIsLoadingParts(true);
		quoteItemApi
			.getCancelableQuoteItems({ guidQuote })
			.then((response) => {
				const {
					data: { result },
				} = response;
				const allGuidsUnaswared = result.cancelableQuoteItem.map((item) => item.guidQuoteItem);
				const allGuidsAbovePriceCap = result.cancelableAbovePriceCapQuoteItem.map((item) => item.guidQuoteItem);

				const allGuids = [...allGuidsUnaswared, ...allGuidsAbovePriceCap];

				setDataParts(result);
				setOptionsSelected(allGuids);
			})
			.finally(() => {
				setIsLoadingParts(false);
			});
	}

	async function onSubmit() {
		if (!validateForm()) return;
		setOpenModalConfirmCancel(true);
	}
	//#endregion

	//#region Handler Functions
	function handleToggleCheckboxAll() {
		if (isNullUndefined(dataParts) || isLoadingSave) return;

		const objectDataParts = Object.entries(dataParts);
		const allCheckboxesSelected = objectDataParts.every(([, valueParam]) => {
			const value = valueParam as CancelableQuoteItemResponse[];
			return value.every((item) => optionsSelected.includes(item.guidQuoteItem));
		});

		if (allCheckboxesSelected) {
			setOptionsSelected([]);
		} else {
			const allIdsUnaswered = dataParts.cancelableQuoteItem.map((item) => item.guidQuoteItem);
			const allIdsAbovePriceCap = dataParts.cancelableAbovePriceCapQuoteItem.map((item) => item.guidQuoteItem);

			const allIds = [...allIdsUnaswered, ...allIdsAbovePriceCap];
			setOptionsSelected(allIds);
		}
	}

	function handleToggleCheckbox(idPart: string | string[]) {
		const cloneOptionsSelected = lodash.cloneDeep(optionsSelected);

		const isIdPartArray = Array.isArray(idPart);

		if (!isIdPartArray) {
			const index = cloneOptionsSelected.indexOf(idPart);

			if (index > -1) {
				cloneOptionsSelected.splice(index, 1);
			} else {
				cloneOptionsSelected.push(idPart);
			}
		} else {
			idPart.forEach((id) => {
				const index = cloneOptionsSelected.indexOf(id);
				if (index > -1) {
					cloneOptionsSelected.splice(index, 1);
				} else {
					cloneOptionsSelected.push(id);
				}
			});
		}

		setOptionsSelected(cloneOptionsSelected);
	}

	//#endregion
	useEffect(() => {
		if (isOpenModal) {
			fetchPartsCancel();
		}
	}, [isOpenModal]);

	//#region Mini Components
	const AreaSelectAll = () => {
		if (!isLoadingParts && isNullUndefined(dataParts)) return null;

		return (
			<S.AreaSelectAll>
				{isLoadingParts ? (
					<>
						<SkeletonLoading $height="10px" $forceWidth="100px" />
						<SkeletonLoading $height="20px" $forceWidth="20px" $margin="0 auto" />
					</>
				) : (
					<FormControlLabel
						label={getTranslateByKey("information.selectAll")}
						labelPlacement="start"
						control={
							<KogCheckbox
								id={`id-createOrder-checkbox-selectAll`}
								disabled={isLoadingSave}
								checked={allCheckboxesSelected}
								value={allCheckboxesSelected}
								onChange={() => handleToggleCheckboxAll()}
							/>
						}
					/>
				)}
			</S.AreaSelectAll>
		);
	};

	//#endregion

	return (
		<S.Container>
			<S.RowHeader>
				<h4>{getTranslateByKey("information.cancelParts")}</h4>
				<AreaSelectAll />
			</S.RowHeader>

			<S.RowBody>
				<AreaPartsSelectCancel
					type="unanswered"
					dataParts={dataParts?.cancelableQuoteItem}
					handleToggleCheckbox={handleToggleCheckbox}
					isLoadingParts={isLoadingParts}
					numbersParts={(cartDataUnansweredItems?.parts ?? []).length}
					optionsSelected={optionsSelected}
				/>
				<AreaPartsSelectCancel
					type="abovePrive"
					dataParts={dataParts?.cancelableAbovePriceCapQuoteItem}
					handleToggleCheckbox={handleToggleCheckbox}
					isLoadingParts={isLoadingParts}
					numbersParts={(cartNoSupplierAssigned?.parts ?? []).length}
					optionsSelected={optionsSelected}
				/>
			</S.RowBody>

			<S.AreaButtons>
				<SecondaryButton id="id-createOrder-btn-back" type="button" onClick={closeModal}>
					{getTranslateByKey("information.back")}
				</SecondaryButton>
				<SecondaryButton id="id-createOrder-btn-save" type="button" isLoading={isLoadingSave} onClick={onSubmit}>
					{getTranslateByKey("information.save")}
				</SecondaryButton>
			</S.AreaButtons>
			<ModalConfirmCancelPart
				type="unasweredBulkCancel"
				dataCart={dataCart}
				refreshQuoteByGetItems={refreshQuoteByGetItems}
				guidQuote={guidQuote}
				guidsQuoteItem={optionsSelected}
				openModalConfirm={openModalConfirmCancel}
				setOpenModalConfirm={setOpenModalConfirmCancel}
				callbackAfterRemovePart={() => closeModal()}
			/>
		</S.Container>
	);
}

function WrapperModalCancelParts(props: IModalCancelPartsProps) {
	return (
		<KogModalSideComponent shouldRenderOpen open={props.isOpenModal} closeModal={() => props.setIsOpenModal(false)}>
			<ModalCancelParts {...props} />
		</KogModalSideComponent>
	);
}

export default WrapperModalCancelParts;
